import React, { useEffect, useState } from 'react';
import PaginatedTable from '../../commonComponents/PaginatedTable';
import { useCategoryListFetch, useDownloadExcelReshootRequest, useFetchTypeList, useGetReshootRequest, useApproveRequest, useRejectRequest } from '../../hooks/useReshootRequest';
import useLogout from '../../hooks/useLogout';
import { useFetchCompanyCode } from '../../hooks/useTasks';
import { Select, Input, Button, message, Popconfirm, Modal, Card, Space, Typography, Form, Tag } from 'antd';
import { EyeTwoTone, ClockCircleOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import CustomDatePicker from '../../commonComponents/CustomDatePicker';
import SkuDetails from '../../components/EansDetails/SkuDetails';
import { useGetSkuRequestsHistory } from '../../hooks/useRequestsHistory';

const STATUS_LIST = ['null', 'true', 'false'];
const Stage_List = ['All', 'Pending', 'Approved', 'Rejected', 'Failed'];
const ReshootRequest = () => {
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [skuName, setSkuName] = useState(null);
    const [stage, setStageName] = useState('All');
    const [companyCodeText, setCompanyCodeText] = useState('');
    const [category, setCategory] = useState('');
    const [type, setType] = useState('');
    const [statusList, setStatusList] = useState([...STATUS_LIST]);
    const [status, setStatus] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const { data, isFetching } = useGetReshootRequest(
        pageNumber,
        pageSize,
        skuName,
        stage,
        companyCodeText,
        category,
        type,
        status,
        startDate,
        endDate
    );

    const onSuccess = response => {
        console.log('first', response);
        var blob = new Blob([response.data], {
            type: response.headers['content-type'],
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        let filename = response.headers['content-disposition'].split('filename=')[1].split('.')[0];
        link.setAttribute('download', `${filename}.xlsx`);
        document.body.appendChild(link);
        link.click();
    };
    const { mutate: downloadExcelFileReshoot, isLoading: isExporting } = useDownloadExcelReshootRequest(onSuccess);
    const { logout } = useLogout();

    const FetchCompanyCodeError = err => {
        if (err.response.status === 401) logout();
    };
    const { data: companyCodeList } = useFetchCompanyCode(FetchCompanyCodeError);
    const { data: categoryList } = useCategoryListFetch();
    const { data: typeList } = useFetchTypeList();
    console.log(typeList, 'typeList');

    const [processingRequestId, setProcessingRequestId] = useState(null);
    const [showSkuDetails, setShowSkuDetails] = useState(false);
    const [selectedSkuId, setSelectedSkuId] = useState(null);
    const [showRequestHistory, setShowRequestHistory] = useState(false);
    const [selectedSkuIdForHistory, setSelectedSkuIdForHistory] = useState(null);
    const [requestHistory, setRequestHistory] = useState([]);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedRequestIdForReject, setSelectedRequestIdForReject] = useState(null);
    const [rejectForm] = Form.useForm();

    const onApproveSuccess = () => {
        message.success('Request approved successfully');
        setProcessingRequestId(null);
    };

    const onApproveError = error => {
        const errorMessage = error?.response?.data?.error || 'Failed to approve request';
        message.error(errorMessage);
        setProcessingRequestId(null);
    };

    const onRejectSuccess = () => {
        message.success('Request rejected successfully');
        setProcessingRequestId(null);
        setShowRejectModal(false);
        setSelectedRequestIdForReject(null);
        rejectForm.resetFields();
    };

    const onRejectError = error => {
        const errorMessage = error?.response?.data?.error || 'Failed to reject request';
        message.error(errorMessage);
        setProcessingRequestId(null);
        // Keep modal open on error so user can retry
    };

    const { mutate: approveRequest, isLoading: isApproving } = useApproveRequest(onApproveSuccess, onApproveError);
    const { mutate: rejectRequest, isLoading: isRejecting } = useRejectRequest(onRejectSuccess, onRejectError);

    const onHistorySuccess = data => {
        if (data?.data?.length > 0) {
            setRequestHistory(data?.data);
        } else {
            setRequestHistory([]);
        }
    };

    const onHistoryError = err => {
        console.log(err);
        message.error(err?.response?.data?.message || 'Failed to fetch request history');
        if (err?.response?.status === 401) {
            logout();
        }
    };

    const { isFetching: isFetchingHistory } = useGetSkuRequestsHistory(selectedSkuIdForHistory, onHistorySuccess, onHistoryError);

    const handleApprove = (requestId, isShootable = null) => {
        setProcessingRequestId(requestId);
        approveRequest({ requestId, isShootable });
    };

    const handleRejectClick = requestId => {
        setSelectedRequestIdForReject(requestId);
        setShowRejectModal(true);
        rejectForm.resetFields();
    };

    const handleRejectSubmit = () => {
        rejectForm.validateFields().then(values => {
            const rejectionReason = values.rejectionReason || '';
            setProcessingRequestId(selectedRequestIdForReject);
            rejectRequest({ requestId: selectedRequestIdForReject, rejectionReason });
        });
    };

    const handleRejectCancel = () => {
        setShowRejectModal(false);
        setSelectedRequestIdForReject(null);
        rejectForm.resetFields();
    };

    // Get action buttons configuration based on request type
    const getActionButtons = requestType => {
        const buttonConfigs = {
            InventoryDamage: [
                { label: 'Mark as Shootable', action: 'approve', isShootable: true, type: 'primary' },
                { label: 'Mark as Non-Shootable', action: 'approve', isShootable: false, type: 'default' },
                { label: 'Reject', action: 'reject', type: 'danger' },
            ],
            NonShootable: [
                { label: 'Mark as Shootable', action: 'approve', isShootable: true, type: 'primary' },
                { label: 'Mark as Non-Shootable', action: 'approve', isShootable: false, type: 'default' },
                { label: 'Reject', action: 'reject', type: 'danger' },
            ],
            MarkSkuAsSupport: [
                { label: 'Mark as Support', action: 'approve', type: 'primary' },
                { label: 'Reject', action: 'reject', type: 'danger' },
            ],
        };

        return buttonConfigs[requestType] || [
            { label: 'Approve', action: 'approve', type: 'primary' },
            { label: 'Reject', action: 'reject', type: 'danger' },
        ];
    };
    const oncompanyCodeChange = value => {
        setCompanyCodeText(value);
        dropdownFilter();
    };

    const onCategoryChange = value => {
        setCategory(value);
        dropdownFilter();
    };

    const onStatusChange = value => {
        setStageName(value);
        dropdownFilter();
    };

    const onTypeChange = value => {
        setType(value);
        dropdownFilter();
    };

    const dropdownFilter = () => {
        setPageNumber(1);
    };

    const OnChangePaginator = (pageNum, pageSize) => {
        setPageNumber(pageNum);
        setPageSize(pageSize);
    };

    const downloadExcelFile = () => {
        downloadExcelFileReshoot({ skuName, clientCode: companyCodeText, category, type, status, from: startDate, to: endDate });
    };

    const checkApprovedStatus = status => {
        if (status) {
            return 'Approved';
        } else if (status === null) {
            return 'Pending';
        } else {
            return 'Rejected';
        }
    };

    const columns = [
        {
            title: 'Sku Name',
            key: 'skuName',
            dataIndex: 'skuName',
        },
        {
            title: 'EAN',
            key: 'EAN',
            dataIndex: 'EAN',
        },
        {
            title: 'Type',
            key: 'requestType',
            dataIndex: 'requestType',
        },
        {
            title: 'Preset / Model',
            key: 'presetModel',
            render: (_, record) => {
                if (record.requestType !== 'PresetChange') return null;
                const preset = record.newAnglePreset;
                const model = record.newModel;
                return (
                    <Space direction="vertical" size={2}>
                        <span>{preset ? `${preset.name}${preset.alias ? ` (${preset.alias})` : ''}` : 'Preset not specified'}</span>
                        {model && <span>{`${model.name} (${model.occassion})`}</span>}
                        <Space size={4} wrap>
                            <Tag color={record.reshootRequired ? 'orange' : 'default'}>
                                Reshoot: {record.reshootRequired ? 'Yes' : 'No'}
                            </Tag>
                            {record.autoApproveDD && <Tag color="blue">Auto DD</Tag>}
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: 'Category',
            key: 'category',
            dataIndex: 'category',
            width: '7%',
        },
        {
            title: 'Comment',
            key: 'requestComment',
            dataIndex: 'requestComment',
        },
        {
            title: 'Creator',
            key: 'requestedBy',
            dataIndex: 'requestedBy',
        },
        {
            title: 'Original PII By',
            key: 'piiBy',
            dataIndex: 'piiBy',
        },
        {
            title: 'Approval Status',
            key: 'approvalStatus',
            render: (_, record) => {
                return <p> {checkApprovedStatus(record.approvalStatus)}</p>;
            },
        },
        {
            title: 'Request Raised At',
            key: 'requestRaisedAt',
            width: '8%',
            render: (_, record) => {
                return (
                    record?.requestRaisedAt && (
                        <p>{`${new Date(record?.requestRaisedAt).toLocaleString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hourCycle: 'h12',
                        })}`}</p>
                    )
                );
            },
        },

        {
            title: 'Request Approved At',
            key: 'requestApprovedAt',
            width: '8%',
            render: (_, record) => {
                return (
                    record?.requestApprovedAt && (
                        <p>{`${new Date(record?.requestApprovedAt).toLocaleString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hourCycle: 'h12',
                        })}`}</p>
                    )
                );
            },
        },
        {
            title: 'Request Processed Status',
            key: 'requestProcessedStatus',
            dataIndex: 'requestProcessedStatus',
        },
        {
            title: 'Action',
            key: 'action',
            width: '15%',
            render: (_, record) => {
                const isProcessing = processingRequestId === record.requestId;
                const isAnyProcessing = processingRequestId !== null;

                // Show action buttons only for pending requests
                if (record.approvalStatus === null) {
                    const actionButtons = getActionButtons(record.requestType);

                    return (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {actionButtons.map((buttonConfig, index) => {
                                if (buttonConfig.action === 'reject') {
                                    return (
                                        <Button
                                            key={index}
                                            danger={buttonConfig.type === 'danger'}
                                            type={buttonConfig.type === 'danger' ? 'default' : buttonConfig.type}
                                            size="small"
                                            loading={isProcessing && isRejecting}
                                            disabled={isAnyProcessing && !isProcessing}
                                            onClick={() => handleRejectClick(record.requestId)}
                                        >
                                            {buttonConfig.label}
                                        </Button>
                                    );
                                } else {
                                    return (
                                        <Button
                                            key={index}
                                            type={buttonConfig.type}
                                            size="small"
                                            onClick={() => handleApprove(record.requestId, buttonConfig.isShootable)}
                                            loading={isProcessing && isApproving}
                                            disabled={isAnyProcessing && !isProcessing}
                                        >
                                            {buttonConfig.label}
                                        </Button>
                                    );
                                }
                            })}
                        </div>
                    );
                }

                // Show existing action information for processed requests
                if (!!record?.actionType) {
                    return (
                        <p>
                            {record?.actionType ?? ''} by {record?.actionBy ?? ''}
                        </p>
                    );
                }

                return <></>;
            },
        },
        {
            title: '',
            key: 'viewDetails',
            width: '8%',
            render: (_, record) => {
                if (record.skuId) {
                    return (
                        <Space size="small">
                            <Button
                                onClick={() => {
                                    setShowSkuDetails(true);
                                    setSelectedSkuId(record.skuId);
                                }}
                                size="small"
                                title="View SKU Details"
                            >
                                <EyeTwoTone />
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowRequestHistory(true);
                                    setSelectedSkuIdForHistory(record.skuId);
                                }}
                                size="small"
                                title="View Request History"
                            >
                                <ClockCircleOutlined />
                            </Button>
                        </Space>
                    );
                }
                return null;
            },
        },
    ];

    const resetFilter = () => {
        setSkuName('');
        setPageNumber(1);
        setCompanyCodeText('');
        setCategory('');
        setType('');
        setStatus('');
        setStartDate('');
        setEndDate('');
    };

    const handleSkuNameSearched = debounce(e => {
        const newValue = e.target.value;
        setSkuName(newValue);
        OnChangePaginator(1, pageSize);
    }, 1500);

    const handleDateChange = ([startDataSelected, endDateSelected]) => {
        setStartDate(startDataSelected);
        setEndDate(endDateSelected);
        dropdownFilter();
    };
    return (
        <div>
            <div className="d-flex reshoot-header">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <h5>Approval Date</h5>
                    <div>
                        <CustomDatePicker className="filter_dropdown-reshoot" onChange={handleDateChange} startDate={startDate} endDate={endDate} />
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <h5>Status</h5>
                    <div>
                        <Select
                            placeholder="Select status"
                            onChange={text => onStatusChange(text)}
                            className="filter_dropdown-reshoot"
                            value={stage}
                            size="small"
                            style={{ minWidth: '100px' }}
                        >
                            {/* <Select.Option key="none" value={''} size="small">
                                All
                            </Select.Option> */}
                            {Stage_List?.map(el => {
                                return (
                                    <Select.Option key={el} value={el}>
                                        {/* {el === 'null' ? 'Pending' : el === 'true' ? 'Approved' : el === 'false' ? 'Rejected' : ''} */}
                                        {el}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <h5>Type</h5>
                    <div>
                        <Select
                            placeholder="Select category"
                            onChange={text => onTypeChange(text)}
                            className="filter_dropdown-reshoot"
                            value={type}
                            size="small"
                            style={{ minWidth: '180px' }}
                        >
                            <Select.Option key="none" value={''} size="small">
                                All
                            </Select.Option>
                            {typeList?.data?.types.map(el => {
                                return (
                                    <Select.Option key={el.id} value={el}>
                                        {el === '' ? 'un category' : el}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <h5>Client</h5>
                    <Select
                        showSearch
                        placeholder="Select company code"
                        onChange={text => oncompanyCodeChange(text)}
                        className="filter_dropdown-reshoot"
                        value={companyCodeText}
                        size="small"
                        style={{ minWidth: '80px' }}
                    >
                        <Select.Option key="none" value={''} size="small">
                            All
                        </Select.Option>
                        {companyCodeList?.map(el => {
                            return (
                                <Select.Option key={el.id} value={el}>
                                    {el}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </div>
            </div>
            <div className="d-flex reshoot-header">
                <div
                    style={{
                        width: '180px',
                        margin: '5px',
                    }}
                >
                    <Input
                        size="small"
                        placeholder="Search by sku name"
                        name="search"
                        onChange={e => {
                            handleSkuNameSearched(e);
                        }}
                        // value={skuName}
                        allowClear
                    />
                </div>
                <div>
                    <Button type="primary" size="small" loading={isExporting} onClick={() => downloadExcelFile()}>
                        Export Data
                    </Button>
                </div>
                <div className="">
                    <Button type="primary" size="small" onClick={() => resetFilter()}>
                        Reset Filter
                    </Button>
                </div>
            </div>
            <PaginatedTable
                dataSource={data?.data}
                columns={columns}
                isFetching={isFetching}
                total={data?.totalCount}
                OnChangePaginator={OnChangePaginator}
                defaultPageSize={20}
                pageNum={pageNumber}
                pageSize={pageSize}
                height={'calc(100vh - 185px)'}
                paginationMaxHeight={'20px'}
            />
            {selectedSkuId && showSkuDetails && (
                <SkuDetails
                    onCancel={() => {
                        setShowSkuDetails(false);
                        setSelectedSkuId(null);
                    }}
                    open={showSkuDetails}
                    skuId={selectedSkuId}
                    selectedSKU={null}
                />
            )}
            <Modal
                title="Request History"
                open={showRequestHistory}
                onCancel={() => {
                    setShowRequestHistory(false);
                    setSelectedSkuIdForHistory(null);
                    setRequestHistory([]);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setShowRequestHistory(false);
                        setSelectedSkuIdForHistory(null);
                        setRequestHistory([]);
                    }}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {isFetchingHistory ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                ) : requestHistory?.length > 0 ? (
                    <Space
                        style={{ width: '100%', marginTop: '1rem', padding: 0, position: 'relative', maxHeight: '500px', overflow: 'auto' }}
                        direction="vertical"
                        size={8}
                    >
                        {[...requestHistory].reverse().map((item, index) => {
                            return (
                                <Card
                                    key={index}
                                    size="small"
                                    title={<div style={{ fontWeight: 'bold' }}>{item?.requestType}</div>}
                                    extra={
                                        <div style={{ fontSize: 13 }}>
                                            <strong>Raised By:</strong> {item?.raisedBy}
                                        </div>
                                    }
                                >
                                    <div style={{ fontSize: 13 }}>
                                        <div>
                                            <strong>Comment:</strong> {item?.comment || 'N/A'}
                                        </div>
                                        {item?.requestType === 'PresetChange' && (
                                            <>
                                                <div>
                                                    <strong>Preset:</strong>{' '}
                                                    {item?.newAnglePreset
                                                        ? `${item.newAnglePreset.name}${item.newAnglePreset.alias ? ` (${item.newAnglePreset.alias})` : ''}`
                                                        : 'Not specified'}
                                                </div>
                                                <div>
                                                    <strong>Model:</strong>{' '}
                                                    {item?.newModel ? `${item.newModel.name} (${item.newModel.occassion})` : 'Not specified'}
                                                </div>
                                                <div>
                                                    <strong>Reshoot Required:</strong> {item?.reshootRequired ? 'Yes' : 'No'}
                                                </div>
                                                <div>
                                                    <strong>Auto-approve DD:</strong> {item?.autoApproveDD ? 'Yes' : 'No'}
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <strong>Raised At:</strong> {item?.creationTimestamp ? new Date(item?.creationTimestamp).toLocaleString() : 'N/A'}
                                        </div>
                                        <div>
                                            <strong>Status:</strong> {item?.stage || 'N/A'}
                                        </div>
                                        <div>
                                            <strong>Action:</strong> {item?.actionType ? `${item?.actionType} by ${item?.actionBy || 'N/A'}` : 'Pending'}
                                        </div>
                                        {item?.actionTimestamp && (
                                            <div>
                                                <strong>{item?.actionType || 'Action'} at:</strong> {new Date(item?.actionTimestamp).toLocaleString()}
                                            </div>
                                        )}
                                        {item?.rejectionReason && (
                                            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff2f0', borderRadius: '4px' }}>
                                                <strong>Rejection Reason:</strong> {item.rejectionReason}
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </Space>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>No request history found</div>
                )}
            </Modal>
            <Modal
                title="Reject Request"
                open={showRejectModal}
                onCancel={handleRejectCancel}
                footer={[
                    <Button key="cancel" onClick={handleRejectCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        onClick={handleRejectSubmit}
                        loading={isRejecting && processingRequestId === selectedRequestIdForReject}
                    >
                        Reject
                    </Button>,
                ]}
                width={600}
            >
                <Form form={rejectForm} layout="vertical">
                    <Form.Item
                        name="rejectionReason"
                        label="Rejection Reason"
                        rules={[
                            {
                                required: true,
                                message: 'Please provide a reason for rejection',
                            },
                            {
                                min: 10,
                                message: 'Rejection reason must be at least 10 characters',
                            },
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Please provide a reason for rejecting this request..."
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ReshootRequest;

// <div
//     style={{
//         display: 'flex',
//         alignItems: 'center',
//         gap: '0.5rem',
//     }}
// >
//     <h5> Category</h5>
//     <div>
//         <Select
//             placeholder="Select category"
//             onChange={text => onCategoryChange(text)}
//             className="filter_dropdown-reshoot"
//             value={category}
//             size="small"
//         >
//             <Select.Option key="none" value={''} size="small">
//                 All
//             </Select.Option>

//             {categoryList?.data?.categories?.map(el => {
//                 return (
//                     <Select.Option key={el.id} value={el}>
//                         {el === '' ? 'un category' : el}
//                     </Select.Option>
//                 );
//             })}
//         </Select>
//     </div>
// </div>;
